/**
 * @file This file is in an "internal" directory to prevent it from being
 * used outside of the `GlobalProvider` component. This is to ensure it
 * doesn't get confused with some more typical hook for getting the `CartVM`
 * from the global context.
 */

import CartVM from '@/react/view-models/CartVM';
import { startTransition, useCallback, useRef, useState } from 'react';
import type { RevalidateGlobalCartFn } from '../RevalidateGlobalCartContext';

/**
 * A utility hook for managing the creation of a new {@link CartVM} and
 * synchronizing it with server-side state on the client.
 * @returns A 2-tuple of a {@link CartVM} instance and a concurrent mode
 * compatible function to revalidate it with the server.
 */
export function useCartState(): [CartVM, RevalidateGlobalCartFn] {
  const [cart, setCart] = useState<CartVM>(() => new CartVM());

  const nextCartRef = useRef<CartVM | null>(null);
  const revalidateGlobalCart = useCallback(() => {
    const newCart = new CartVM();
    nextCartRef.current = newCart;
    // We cannot simply call `refresh()` on the existing CartVM instance
    // because MobX state updates cannot be marked as transitions.
    startTransition(async () => {
      await newCart.refresh();
      // Prevent race condition if `revalidateGlobalCart()` was called multiple times
      if (nextCartRef.current === newCart) {
        // state updates after an async call must be re-wrapped
        // in a transition to be part of the same transition
        startTransition(() => setCart(newCart));
        nextCartRef.current = null; // clean up the reference
      }
    });
  }, []);

  return [cart, revalidateGlobalCart];
}
